import { useCallback, useEffect, useMemo, useState } from "react"
import { ProgramAndCoursesInfo } from "../../../../../types/gqlTypes/graphql"
import { useGenericReducer } from "../../../../mp-core/hooks/useGenericReducer"
import { UserGoalsData } from "../../../types"
import { getAvailableGoals } from "../helpers"

export const useAvailableGoals = (
  programsAndCourses: ProgramAndCoursesInfo[],
  disableFetching: boolean
) => {
  const [availableGoals, setAvailableGoals] = useState<UserGoalsData[]>([])
  const { state: loadingState, stateSetter: loadingStateSetter } =
    useGenericReducer<{
      goalsDataLoading: boolean
      refetchGoalsDataLoading: boolean
    }>({
      goalsDataLoading: true,
      refetchGoalsDataLoading: true
    })

  const availableGoalsTestsMapping: Record<string, string[]> = useMemo(() => {
    return availableGoals.reduce((res, goal) => {
      if (!goal.test || !goal.subtest) return res

      return {
        ...res,
        [goal.test]: Array.from(
          new Set([...(res[goal.test] || []), goal.subtest])
        )
      }
    }, {})
  }, [availableGoals])

  const availableGoalsProgramCourses: ProgramAndCoursesInfo[] = useMemo(() => {
    return programsAndCourses.reduce(
      (res: ProgramAndCoursesInfo[], item: ProgramAndCoursesInfo) => {
        const program = item.program

        if (!availableGoalsTestsMapping[program]) return res

        return [
          ...res,
          {
            ...item,
            courses: item.courses.filter(course =>
              availableGoalsTestsMapping[program].includes(course.name)
            )
          }
        ]
      },
      []
    )
  }, [programsAndCourses, availableGoalsTestsMapping])

  const refetchGoalsData = useCallback(() => {
    loadingStateSetter({ type: "refetchGoalsDataLoading", value: true })
  }, [loadingStateSetter])

  useEffect(() => {
    const fetchAvailableGoals = async () => {
      loadingStateSetter({ type: "goalsDataLoading", value: true })

      try {
        const response = await getAvailableGoals()

        setAvailableGoals(response)
      } catch (error) {
        console.warn("[ReportingContext]", error)
      } finally {
        loadingStateSetter({ type: "goalsDataLoading", value: false })
        loadingStateSetter({ type: "refetchGoalsDataLoading", value: false })
      }
    }

    if (!disableFetching && loadingState.refetchGoalsDataLoading) {
      fetchAvailableGoals()
    }
  }, [disableFetching, loadingState.refetchGoalsDataLoading])

  return {
    availableGoals,
    availableGoalsTestsMapping,
    availableGoalsProgramCourses,
    availableGoalsLoading: loadingState.goalsDataLoading,
    refetchGoalsData
  }
}
